import React, { useMemo, useState } from 'react';
import { Badge, Button, Calendar, Card, DatePicker, Input, Popover, Segmented, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CalendarOutlined, DownloadOutlined, PlusOutlined, UnorderedListOutlined, UserOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import { useRecruitList } from './shared/useRecruitList';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { exportRows, fmtDateTime, stageLabel } from './shared/recruitUi';

const { Text } = Typography;
const FILTERS = ['view', 'status', 'date_from', 'date_to', 'panel_employee_id', 'department_id', 'month'] as const;
const STATUSES = ['Scheduled', 'Rescheduled', 'Completed', 'Cancelled', 'NoShow'];

const InterviewsPage: React.FC = () => {
  const navigate = useNavigate();
  const list = useRecruitList(FILTERS);
  const view = list.filters.view === 'calendar' ? 'calendar' : 'list';
  const month = list.filters.month ? dayjs(`${list.filters.month}-01`) : dayjs().startOf('month');
  const [calMonth, setCalMonth] = useState<Dayjs>(month);

  const listParams = { ...list.apiParams, view: undefined, month: undefined };
  const { data, isFetching } = useQuery({
    queryKey: hrmsKeys.interviews(listParams),
    queryFn: () => hrmsService.pageInterviews(listParams),
    placeholderData: keepPreviousData,
    enabled: view === 'list',
  });

  const calParams = {
    page: 1,
    limit: 1000,
    sortBy: 'scheduled_at_asc',
    status: list.filters.status,
    panel_employee_id: list.filters.panel_employee_id,
    department_id: list.filters.department_id,
    q: list.q || undefined,
    date_from: calMonth.startOf('month').subtract(7, 'day').format('YYYY-MM-DD'),
    date_to: calMonth.endOf('month').add(7, 'day').format('YYYY-MM-DD'),
  };
  const { data: calData } = useQuery({
    queryKey: hrmsKeys.interviews({ ...calParams, cal: 1 }),
    queryFn: () => hrmsService.pageInterviews(calParams),
    enabled: view === 'calendar',
  });
  const byDay = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const r of calData?.rows || []) {
      const k = dayjs(r.scheduled_at).format('YYYY-MM-DD');
      m.set(k, [...(m.get(k) || []), r]);
    }
    return m;
  }, [calData]);

  const exportAll = async () => {
    const all = await hrmsService.pageInterviews({ ...listParams, page: 1, limit: 5000 });
    await exportRows(
      'interviews',
      [
        ['application_number', 'Application #'],
        ['candidate_name', 'Candidate'],
        ['job_title', 'Job'],
        ['round_number', 'Round'],
        ['scheduled_at', 'Scheduled'],
        ['duration_minutes', 'Minutes'],
        ['mode', 'Mode'],
        ['status', 'Status'],
        ['feedback_count', 'Feedback received'],
        ['overall_score', 'Score (1–5)'],
        ['recommendation', 'Recommendation'],
      ],
      all.rows
    );
  };

  const columns: ColumnsType<any> = [
    {
      title: 'When',
      dataIndex: 'scheduled_at',
      width: 160,
      fixed: 'left',
      render: (v, r) => <a onClick={() => navigate(`/hr/interviews/${r.id}`)}>{fmtDateTime(v)}</a>,
    },
    {
      title: 'Candidate',
      dataIndex: 'candidate_name',
      ellipsis: true,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <span>{v}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.application_number}
          </Text>
        </Space>
      ),
    },
    { title: 'Job', dataIndex: 'job_title', ellipsis: true },
    { title: 'Round', dataIndex: 'round_number', width: 70 },
    { title: 'Mode', dataIndex: 'mode', width: 100 },
    {
      title: 'Feedback',
      key: 'fb',
      width: 100,
      render: (_, r) => {
        const total = (r.panel_employee_ids_json || []).length;
        const got = Number(r.feedback_count || 0);
        return <Tag color={got >= total && total ? 'green' : got ? 'gold' : 'default'}>{`${got}/${total}`}</Tag>;
      },
    },
    { title: 'Score', dataIndex: 'overall_score', width: 80, render: (v) => (v != null ? `${Number(v).toFixed(1)}/5` : '—') },
    { title: 'Status', dataIndex: 'status', width: 120, render: (s) => <Tag color={hrmsStatusColor(s)}>{stageLabel(s)}</Tag> },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Interviews"
          subtitle="All interview rounds — schedule, track feedback and close with a panel decision"
          primaryActions={
            <Space>
              <Segmented
                value={view}
                onChange={(v) => list.update({ view: v === 'calendar' ? 'calendar' : undefined }, false)}
                options={[
                  { value: 'list', icon: <UnorderedListOutlined />, label: 'List' },
                  { value: 'calendar', icon: <CalendarOutlined />, label: 'Calendar' },
                ]}
              />
              <Button icon={<UserOutlined />} onClick={() => navigate('/hr/interviews/mine')}>
                My interviews
              </Button>
              {view === 'list' && (
                <Button icon={<DownloadOutlined />} onClick={exportAll}>
                  Export
                </Button>
              )}
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/interviews/new')}>
                Schedule interview
              </Button>
            </Space>
          }
        />
      }
    >
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input.Search allowClear placeholder="Candidate, job, application" defaultValue={list.q} onSearch={list.setQ} style={{ width: 240 }} />
          <Select
            allowClear
            mode="multiple"
            placeholder="Status"
            style={{ minWidth: 180 }}
            value={list.filters.status ? list.filters.status.split(',') : []}
            onChange={(v: string[]) => list.setFilter('status', v.join(',') || undefined)}
            options={STATUSES.map((s) => ({ value: s, label: stageLabel(s) }))}
          />
          <RecruitPeoplePicker
            placeholder="Panel member"
            style={{ width: 200 }}
            value={list.filters.panel_employee_id}
            onChange={(v) => list.setFilter('panel_employee_id', v as string)}
          />
          <DepartmentSelect
            allowClear
            placeholder="Department"
            style={{ width: 220 }}
            value={list.filters.department_id}
            onChange={(v) => list.setFilter('department_id', v)}
          />
          {view === 'list' && (
            <DatePicker.RangePicker
              value={list.filters.date_from ? [dayjs(list.filters.date_from), dayjs(list.filters.date_to)] : null}
              onChange={(r) => list.update({ date_from: r?.[0]?.format('YYYY-MM-DD'), date_to: r?.[1]?.format('YYYY-MM-DD') })}
            />
          )}
        </Space>
      </Card>

      {view === 'calendar' ? (
        <Card size="small">
          <Calendar
            value={calMonth}
            onPanelChange={(d) => {
              setCalMonth(d);
              list.update({ month: d.format('YYYY-MM') }, false);
            }}
            onSelect={(d, info) => {
              if (info.source === 'date') setCalMonth(d);
            }}
            cellRender={(d, info) => {
              if (info.type !== 'date') return info.originNode;
              const items = byDay.get(d.format('YYYY-MM-DD')) || [];
              if (!items.length) return null;
              return (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {items.slice(0, 4).map((r) => (
                    <li key={r.id}>
                      <Popover
                        title={`${r.candidate_name} — round ${r.round_number}`}
                        content={
                          <Space direction="vertical" size={0}>
                            <span>{r.job_title}</span>
                            <span>
                              {dayjs(r.scheduled_at).format('HH:mm')} · {r.duration_minutes || 60} min · {r.mode}
                            </span>
                            <a onClick={() => navigate(`/hr/interviews/${r.id}`)}>Open</a>
                          </Space>
                        }
                      >
                        <Badge
                          status={r.status === 'Completed' ? 'success' : ['Cancelled', 'NoShow'].includes(r.status) ? 'default' : 'processing'}
                          text={
                            <span style={{ fontSize: 12 }} onClick={() => navigate(`/hr/interviews/${r.id}`)}>
                              {dayjs(r.scheduled_at).format('HH:mm')} {r.candidate_name}
                            </span>
                          }
                        />
                      </Popover>
                    </li>
                  ))}
                  {items.length > 4 && (
                    <li>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        +{items.length - 4} more
                      </Text>
                    </li>
                  )}
                </ul>
              );
            }}
          />
        </Card>
      ) : (
        <Table
          rowKey="id"
          loading={isFetching}
          columns={columns}
          dataSource={data?.rows || []}
          scroll={{ x: 1100 }}
          pagination={list.pagination(data?.total || 0)}
          onChange={list.onTableChange}
        />
      )}
    </WorkspaceLayout>
  );
};

export default InterviewsPage;
